import React from "react";
import { getI18n, useTranslation } from "react-i18next";
import "./App.css";
import CookieConsent from "./components/CookieConsent";
import { Icons } from "./components/icons";
import SelectLanguage from "./components/SelectLanguage";
import { Label } from "./components/ui/label";
import { Toaster } from "./components/ui/toaster";
import MenuPage from "./screens/menu";
import CloseAlert from "./screens/menu/CloseAlert";
import TableReservation from "./screens/table-reservation/TableReservation";
import fetchData from "./utils/api";
import { isBase64 } from "./utils/is-base-64";
import { getQueryParams } from "./utils/query-params";
import { ssoAddressAPI, ssoAuthAPI } from "./utils/sso-apis";
import PlaceOrder from "./screens/place-order/PlaceOrder";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import { toast } from "./components/ui/use-toast";
import ErrorBoundary from "./ErrorBoundary";

function App({ token, id, type }: any) {
  const queryParams = getQueryParams();
  const { t } = useTranslation();
  const currentLanguage = getI18n();
  const [open, setOpen] = React.useState<boolean>(false);
  const [languageOpen, setLanguageOpen] = React.useState<boolean>(true);
  const [isLanguageOpen, setIsLanguageOpen] = React.useState<boolean>(
    type === null || type === "table_reservation" ? false : true
  );
  const [restaurantMessage, setRestaurantMessage] = React.useState<any>("");
  const [loading, setLoading] = React.useState<boolean>(true);
  const [cartTotal, setCartTotal] = React.useState(0);
  const [restaurantDetail, setRestaurantDetail] = React.useState<any>();
  const [authToken, setAuthToken] = React.useState("");
  const [currentSelected, setCurrentSelected] = React.useState("menu");
  const [isTableReservation, setIsTableReservation] = React.useState(
    type === "table_reservation" ? true : false
  );
  const [isDineIn, setIsDineIn] = React.useState(
    type === "dine_in" || type === "table_booking" ? true : false
  );
  const [selectedType, setSelectedType] = React.useState(type || "");
  const [tableReservationCart, setTableReservationCart] = React.useState(false);
  const [ssoUserDetails, setSsoUserDetails] = React.useState<any>(null);
  const [ssoAddress, setSsoAddress] = React.useState<any>(null);
  const [wholeContent, setWholeContent] = React.useState<any>(null);
  const [menuCategories, setMenuCategories] = React.useState<any>([]);
  const [paymentConfirmed, setPaymentConfirmed] = React.useState(false);
  const [isPlaceOrder, setIsPlaceOrder] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState("");
  const [isHeader, setIsHeader] = React.useState(true);

  React.useEffect(() => {
    if (restaurantMessage !== "") {
      setRestaurantMessage(wholeContent[currentLanguage.language]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage.language]);

  const handleAuth = async ({ token, id }) => {
    try {
      const body: any = { token, id };
      const res = await fetchData({ url: "auth", method: "POST", body });
      if (res.status_code === 200) {
        setLoading(false);
        setAuthToken(res.data.token);
        setRestaurantDetail(res.data.restaurant);
        setRestaurantMessage(
          res.data.restaurant_message[currentLanguage.language]
        );
        setWholeContent(res.data.restaurant_message);
        if (selectedType === "dine_in") {
          setOpen(
            res.data.restaurant_message[currentLanguage.language] !== ""
              ? true
              : false
          );
        }

        const cachedCart = localStorage.getItem("cart");
        if (cachedCart !== null) {
          const bodyData = {
            cart_id: cachedCart,
          };
          const cartItemTotal = await fetchData({
            url: "cart/total",
            method: "POST",
            body: bodyData,
            token: res.data.token,
          });
          if (cartItemTotal.status_code === 200) {
            setCartTotal(cartItemTotal.data);
          }
        } else {
          const cachedCart = localStorage.getItem("cart");

          try {
            const body: any = { cart_id: cachedCart };
            const checkCartRes = await fetchData({
              url: "cart/check-cart",
              method: "POST",
              body,
              token: res.data.token,
            });
            if (res.status_code === 200) {
              localStorage.setItem("cart", checkCartRes.data.id);
            }
          } catch (error) {
            console.log("error", error);
          }
        }
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleSSO = async ({ token }) => {
    const auth = await ssoAuthAPI({ token });
    if (auth) {
      setSsoUserDetails(auth);
    }
  };

  const handleSSOAddress = async ({ token }) => {
    const address = await ssoAddressAPI({ token });
    if (address) {
      setSsoAddress(address);
    }
  };

  React.useEffect(() => {
    if (token && id) {
      // const cookie: any = Cookies.get("sparissimo_token");
      let ssoToken = localStorage.getItem("return_token");
      // if (cookie) {
      //   const params = new URLSearchParams(window.location.search);
      //   params.set("return_token", cookie);
      //   window.history.replaceState(
      //     {},
      //     "",
      //     `${window.location.pathname}?${params.toString()}`
      //   );
      //   localStorage.setItem("return_token", cookie);
      //   ssoToken = cookie;
      // }

      if (ssoToken) {
        const params = new URLSearchParams(window.location.search);
        params.set("return_token", ssoToken);
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${params.toString()}`
        );
        localStorage.setItem("return_token", ssoToken);
      }
      const params = new URLSearchParams(window.location.search);
      const return_token = params.get("return_token");

      if (return_token) {
        localStorage.setItem("return_token", return_token);
        ssoToken = return_token;
      }

      if (ssoToken && isBase64(ssoToken)) {
        const parsedCookie = atob(ssoToken);
        handleSSO({ token: parsedCookie });
        handleSSOAddress({ token: parsedCookie });
        // Continue with your logic using parsedCookie
      } else {
        localStorage.removeItem("return_token");
        const params = new URLSearchParams(window.location.search);
        params.delete("return_token");
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${params.toString()}`
        );
        console.error("Invalid base64 string");
        // Handle the error case
      }
      // runOneSignal();
      handleAuth({ token, id });
    }
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get("utm_source");
    const utm_medium = params.get("utm_medium");
    const utm_campaign = params.get("utm_campaign");

    if (utm_source && utm_medium && utm_campaign) {
      localStorage.setItem(
        "utm_parameters",
        JSON.stringify([
          {
            utm_source: utm_source,
            utm_medium: utm_medium,
            utm_campaign: utm_campaign,
          },
        ])
      );
    } else {
      localStorage.removeItem("utm_parameters");
    }

    cleanupUrlParams();
  }, [token, id]);

  const handleOrderMethod = async ({ method }) => {
    const cachedCart = localStorage.getItem("cart");
    try {
      const body: any = {
        cart_id: cachedCart,
        ordering_method: method,
      };
      const res = await fetchData({
        url: "cart/change-ordering-method",
        method: "POST",
        body,
        token: authToken,
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  React.useEffect(() => {
    const cachedCart = localStorage.getItem("cart");
    if (authToken && cachedCart) {
      if (queryParams.type === "table_booking") {
        setSelectedType("table_reservation");
        handleOrderMethod({ method: "table_reservation" });
      } else if (queryParams.type !== null) {
        setSelectedType(queryParams.type);
        handleOrderMethod({ method: queryParams.type });
      }
      if (queryParams.type === "table_reservation") {
        setIsTableReservation(true);
      }
    }
    cleanupUrlParams();
  }, [queryParams.type, authToken]);

  const cleanupUrlParams = () => {
    const baseUrl = window.location.origin + window.location.pathname;

    const queryParams = new URLSearchParams(window.location.search);

    queryParams.delete("payment_intent");
    queryParams.delete("payment_intent_client_secret");
    queryParams.delete("redirect_status");
    queryParams.delete("language");

    const remainingParams = queryParams.toString();
    const newUrl = remainingParams ? `${baseUrl}?${remainingParams}` : baseUrl;

    window.history.replaceState({}, document.title, newUrl);
  };

  return (
    <div className="bg-body-bg">
      {loading ? (
        <div className="flex justify-center items-center h-screen flex-col">
          <Icons.spinner className=" size-7 animate-spin" />
          <h2>{t("please_wait")}</h2>
          <p className="text-sm mt-2 text-center">
            {t("order_history.did_you_know")}
          </p>
        </div>
      ) : isTableReservation ? (
        !restaurantDetail.services.table_reservation ? (
          <div className="flex justify-center items-center h-screen ">
            <Label>{t("table_reservation.not_found")}</Label>
          </div>
        ) : (
          <ErrorBoundary>
            <div>
              {!open && selectedType !== "" && isLanguageOpen && (
                <CookieConsent restaurantDetail={restaurantDetail} />
              )}
              <CloseAlert
                message={restaurantMessage}
                setOpen={setOpen}
                open={open}
              />

              <TableReservation
                authToken={authToken}
                id={id}
                restaurantDetail={restaurantDetail}
                currentSelected={currentSelected}
                setCurrentSelected={setCurrentSelected}
                setIsTableReservation={setIsTableReservation}
                tableReservationCart={tableReservationCart}
                setTableReservationCart={setTableReservationCart}
                setIsDineIn={setIsDineIn}
                ssoUserDetails={ssoUserDetails}
                ssoAddress={ssoAddress}
              />
              <Toaster />
            </div>
          </ErrorBoundary>
        )
      ) : (
        <ErrorBoundary>
          <div>
            {!open && selectedType !== "" && isLanguageOpen && (
              <CookieConsent restaurantDetail={restaurantDetail} />
            )}
            <CloseAlert
              message={restaurantMessage}
              setOpen={setOpen}
              open={open}
            />
            <MenuPage
              authToken={authToken}
              id={id}
              cartTotal={cartTotal}
              setCartTotal={setCartTotal}
              restaurantDetail={restaurantDetail}
              restaurantMessage={restaurantMessage}
              currentSelected={currentSelected}
              setCurrentSelected={setCurrentSelected}
              tableReservationCart={tableReservationCart}
              isDineIn={isDineIn}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              ssoUserDetails={ssoUserDetails}
              ssoAddress={ssoAddress}
              handleSSOAddress={handleSSOAddress}
              open={open}
              setRestaurantDetail={setRestaurantDetail}
              isLanguageOpen={isLanguageOpen}
              setIsTableReservation={setIsTableReservation}
              menuCategories={menuCategories}
              setMenuCategories={setMenuCategories}
            />
            <Toaster />
          </div>
        </ErrorBoundary>
      )}
      {!loading && isPlaceOrder !== true && (
        <SelectLanguage
          open={isLanguageOpen}
          setIsOpen={setIsLanguageOpen}
          isOpen={languageOpen}
          setOpen={setLanguageOpen}
          authToken={authToken}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          restaurantDetail={restaurantDetail}
          setRestaurantMessage={setOpen}
          restaurantMessage={wholeContent}
        />
      )}
    </div>
  );
}

export default App;
